import React, { useMemo, useState } from 'react';
import {
  Button,
  Combobox,
  Grid,
  Group,
  Input,
  InputBase,
  Loader,
  Stack,
  Text,
  useCombobox
} from '@mantine/core';
import { IDynamicPerson } from '@microsoft/mgt-react';
import { PersonByEmail } from '../../PersonByEmail';
import {
  ApprovalMemberFilters,
  PrismLead,
  useApproveFinanceLeadMutation,
  useApproveInvestmentAnalystMutation,
  useApproveTransactionLeadMutation,
  useGetPrismLeadsQuery,
  useUpdateTransactionTeamLeadMutation
} from '@repo/services';
import {
  DeviceType,
  errorBoundaryWrapper,
  filterApprovalMembersList,
  useAgMutation,
  useDeviceDetection
} from '@repo/utils';
import { IconCircleCheck, IconCircleMinus, IconThumbUp } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import TransactionTeamApprovalModal from './TransactionTeamApprovalModal';

/**
 * Rendered outside the parent component so its type identity is stable across
 * renders. Declaring it inline caused React to treat it as a new component type
 * on every render, unmounting/remounting the underlying PersonByEmail and
 * restarting its Graph loading state (the visible flicker).
 */
function SelectOption({ email, name }: any) {
  return (
    <PersonByEmail
      //@ts-ignore
      view={'oneline'}
      avatarSize='large'
      fallbackDetails={{ mail: 'Loading Team Member...', displayName: name } as IDynamicPerson}
      personCardInteraction={'click'}
      email={email || ''}
    />
  );
}

interface TransactionTeamApproverCardProps {
  hasApproverRole: boolean;
  filters: ApprovalMemberFilters[];
  txId: number | undefined;
  txTeamName: string | undefined;
  employeeActor: string | undefined;
  label: string | undefined;
  role: string | undefined;
  canUpdate: boolean;
  approved: boolean;
  approvedDate: string;
  approvedBy: string;
  /**
   * Transaction total price in USD. Used only for the Transaction Lead ("land")
   * role to decide whether low-value-only leads are eligible. Omit/undefined when
   * unknown — eligibility then falls back to the standard pool.
   */
  transactionTotalUsd?: number | null;
}

/**
 * Displays and manages the assigned lead (transaction, finance, or operations) for a transaction team, allowing an authorized approver to reassign the lead via a combobox or approve the current assignment through a confirmation modal.
 *
 * @param hasApproverRole - Whether the current user has permission to approve this role.
 * @param filters - Filters used to narrow the list of eligible approval members.
 * @param txId - ID of the transaction team.
 * @param txTeamName - Display name of the transaction team.
 * @param employeeActor - Username of the employee currently assigned as lead.
 * @param label - Display label for this lead role (e.g. "Transaction Lead").
 * @param role - The role key, one of "land", "finance", or "operations".
 * @param canUpdate - Whether the lead assignment can currently be changed.
 * @param approved - Whether the current lead has been approved.
 * @param approvedDate - Date the lead was approved.
 * @param approvedBy - Name of the user who approved the lead.
 * @param transactionTotalUsd - Transaction total price in USD, used to widen Transaction Lead eligibility on low-value deals.
 */
const TransactionTeamApproverCard = ({
  hasApproverRole,
  filters,
  txId,
  txTeamName,
  employeeActor,
  label,
  role,
  canUpdate,
  approved,
  approvedDate,
  approvedBy,
  transactionTotalUsd
}: TransactionTeamApproverCardProps) => {
  const { data: prismLeads = [], isLoading: prismLeadsLoading } = useGetPrismLeadsQuery(null);
  const device = useDeviceDetection();

  // const { data: employees = [], isLoading: employeesLoading } = useEmployeesQuery(null);
  const [selectedEmployee, setSelectedEmployee] = useState<string | undefined>(employeeActor);
  const [approveModalOpen, setApproveModalOpen] = useState<boolean>(false);
  const [approveButtonDisabled, setApproveButtonDisabled] = useState<boolean>(false);
  const {
    trigger: triggerReplaceTransactionTeamLead,
    isLoading: replaceTransactionTeamLeadIsLoading,
  } = useAgMutation<void>(useUpdateTransactionTeamLeadMutation,
      () => {
        notifications.show({
          title: 'Update Lead Successful',
          message: `You successfully updated transaction team leads for this transaction!`
      });
  });

  const {
    trigger: triggerApproveTransactionLead,
    isLoading: approveTransactionIsLoading,
  } = useAgMutation<void>(useApproveTransactionLeadMutation,
    () => {
      notifications.show({
        title: 'Approve Transaction Lead Successful',
        message: `You successfully approved the Transaction Lead for this transaction!`
      });
    });

  const {
    trigger: triggerApproveFinanceLead,
    isLoading: approveFinanceLeadIsLoading,
  } = useAgMutation<void>(useApproveFinanceLeadMutation,
    () => {
      notifications.show({
        title: 'Approve Finance Lead Successful',
        message: `You successfully approved the Finance Lead for this transaction!`
      });
    });

  const {
    trigger: triggerApproveInvestmentAnalyst,
    isLoading: approveInvestmentAnalystIsLoading,
  } = useAgMutation<void>(useApproveInvestmentAnalystMutation,
    () => {
      notifications.show({
        title: 'Approve Investment Analyst Successful',
        message: `You successfully approved the Investment Analyst for this transaction!`
      });
    });

  const toggleApproval = async () => {
    if (txId && selectedEmployee && role) {
      if (role === 'land') {
        triggerApproveTransactionLead(txId);
      } else if (role === 'finance') {
        triggerApproveFinanceLead(txId);
      } else if (role === 'operations') {
        triggerApproveInvestmentAnalyst(txId)
      } else {
        notifications.show({
          title: 'Problem approving lead',
          message: 'There was a problem approving lead. No matching role found.',
          color: 'red'
        })
      }
      setApproveModalOpen(false);
    }
  };

  const handleReplaceLead = (val: any) => {
    let roleName = '';
    if (role === 'finance') roleName = 'FinanceLead';
    else if (role === 'operations') roleName = 'InvestmentAnalyst';
    else if (role === 'land') roleName = 'TransactionLead';

    const lead = prismLeads?.find((l) => l.userName === val);

    if (roleName && lead) {
      const updateObj = {
        roleName: roleName,
        leadUserName: val,
        leadFullName: lead.fullName
      }
      setSelectedEmployee(val);
      combobox.closeDropdown();

      triggerReplaceTransactionTeamLead({
        id: txId,
        payload: updateObj
      });
    }
  }

  const filteredLeads: PrismLead[] = useMemo(() => {
    if (prismLeads && prismLeads.length > 0) {
      return filterApprovalMembersList(prismLeads, role, transactionTotalUsd);
    }
    return prismLeads;
  }, [prismLeads, role, transactionTotalUsd]);

  const filteredLeadOptions = useMemo(() => {
    return filteredLeads?.map((lead) => {
      return { value: lead?.userName, email: lead?.email, name: lead?.fullName };
    });
  }, [filteredLeads]);

  const allOptions = useMemo(() => {
    return prismLeads?.map((lead) => {
      return { value: lead?.userName, email: lead?.email, name: lead?.fullName };
    });
  }, [prismLeads])


  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption()
  });

  const selectedOption = useMemo(() => {
    if (employeeActor && !selectedEmployee && allOptions?.length) {
      return allOptions.find((item) => item.value.includes(employeeActor));
    }
    if (selectedEmployee && allOptions?.length) {
      return allOptions.find((item) => item.value.includes(selectedEmployee));
    }
  }, [employeeActor, allOptions, selectedEmployee]);

  const options = filteredLeadOptions?.map((item) => (
    //@ts-ignore
    <Combobox.Option value={item?.value} key={item?.value}>
      <SelectOption {...item} />
    </Combobox.Option>
  ));

  const timeStampText = useMemo(() => {
    let returnText = '';
    const isYes = approved;
    const formattedDecisionDate = dayjs(approvedDate || '').format('MM-DD-YYYY');
    if (isYes && selectedOption) {
      returnText = `${approvedBy} on ${formattedDecisionDate}`;
    }
    return returnText;
  },[approved, approvedBy, selectedOption])

  const closeApprovalModal = () => setApproveModalOpen(false);

  return (
    <>
      <TransactionTeamApprovalModal employeeName={selectedOption?.name} closeApprovalModal={closeApprovalModal} toggleApproval={toggleApproval} txId={txId} txTeamName={txTeamName} approveModalOpen={approveModalOpen} />
      <div style={{ maxWidth: 800 }}>
        <Stack>
          <Text>{label}</Text>
          <Grid>
            <Grid.Col pr={window.innerWidth > 500 ? 0 : undefined} span={{ xs: 12, sm: 7}}>
              <Combobox
                store={combobox}
                withinPortal={false}
                onOptionSubmit={(val) => handleReplaceLead(val)}
              >
                <Combobox.Target>
                  <InputBase
                    component='button'
                    type='button'
                    pointer
                    size='lg'
                    styles={(theme) => ({
                      input: {
                        borderBottomLeftRadius: '10px',
                        borderTopLeftRadius: '10px',
                        borderBottomRightRadius: 0,
                        borderTopRightRadius: 0,
                        height: '80px'
                      }
                    })}
                    rightSection={!approved ? <Combobox.Chevron /> : <></>}
                    onClick={() => combobox.toggleDropdown()}
                    rightSectionPointerEvents='none'
                    multiline
                  >
                    {/*{employeesLoading && !selectedOption && (*/}
                    {/*    <Loader />*/}
                    {/*)}*/}
                    {selectedOption ? (
                      <SelectOption {...selectedOption} />
                    ) : (
                      <Input.Placeholder>
                        <Loader />
                      </Input.Placeholder>
                    )}
                  </InputBase>
                </Combobox.Target>

                {canUpdate && (
                  <Combobox.Dropdown>
                    <Combobox.Options mah={200} style={{ overflowY: 'auto' }}>
                      {options}
                    </Combobox.Options>
                  </Combobox.Dropdown>
                )}
              </Combobox>
            </Grid.Col>
            <Grid.Col pl={window.innerWidth > 500 ? 0 : undefined} span={{ xs: 12, sm: 5}} style={{ textAlign: 'center' }}>
              {!approved && hasApproverRole && (
                <Button
                  variant='default'
                  h={'80px'}
                  w='100%'
                  disabled={approveButtonDisabled}
                  style={{ borderBottomLeftRadius: 0, borderTopLeftRadius: 0, fontSize: '16px' }}
                  rightSection={replaceTransactionTeamLeadIsLoading ? <></> : <IconThumbUp size={24} />}
                  onClick={() => setApproveModalOpen(true)}
                >
                  {replaceTransactionTeamLeadIsLoading ? <Loader /> : <>Approve</>}
                </Button>
              )}
              {!approved && !hasApproverRole && (
                  <Button
                      color='gray'
                      h={'80px'}
                      w='100%'
                      style={{ borderBottomLeftRadius: 0, borderTopLeftRadius: 0, fontSize: '16px' }}
                  >
                    <Stack gap='xs' justify='center'>
                      <Group justify='center'>
                        Pending Approval
                        <IconCircleMinus size={24} />
                      </Group>
                      {/*<Text size='xs' w={'80%'} style={{ textWrap: 'wrap'}} fs="italic">{timeStampText}</Text>*/}
                    </Stack>
                  </Button>
              )}
              {approved && (
                <Button
                  color='green'
                  h={'80px'}
                  w='100%'
                  style={{ borderBottomLeftRadius: 0, borderTopLeftRadius: 0, fontSize: '16px' }}
                >
                  <Stack gap='xs' justify='center'>
                    <Group justify='center'>
                      Approved
                      <IconCircleCheck size={24} />
                    </Group>
                    <Text size='xs' w={'80%'} style={{ textWrap: 'wrap'}} fs="italic">{timeStampText}</Text>
                  </Stack>
                </Button>
              )}
            </Grid.Col>
          </Grid>
        </Stack>
      </div>
    </>
  );
};

export default errorBoundaryWrapper(TransactionTeamApproverCard);
